import React from "react";
import PropTypes from "prop-types";
import { LOCAL_EN, LOCAL_FR } from "../../../const/const";
import { FormattedMoney } from "../../FormattedMoney";
import { StarRating } from "../../StarRating";
// eslint-disable-next-line import/no-cycle
import { Card } from "../Card";
// eslint-disable-next-line import/no-cycle
import { CardBuilder, LogoPositions } from "../CardComponents/CardBuilder";
import { HeadingAsElement, HeadingSize } from "../../Heading";
import { CardAsType, CardImageRatio } from "../CardComponents/CardType/CardType";
import * as styles from "./Hotel.module.scss";

const HotelCard = ({
	clickable,
	type,
	image,
	images,
	headAs,
	headSize,
	renderImage,
	link,
	renderLink,
	header,
	logo,
	logoPosition,
	headingSize,
	headingAs,
	subheader,
	stars,
	minAge,
	days,
	save,
	wasPrice,
	price,
	local,
	labels,
	isSponsored,
	hotelTags,
	sliderNav,
	ratio,
}) => (
	<CardBuilder
		clickable={clickable}
		type={type}
		images={image ? [image, ...images] : [...images]}
		headAs={headAs}
		headSize={headSize}
		logo={logo}
		logoPosition={logoPosition}
		isSponsored={isSponsored}
		hotelTags={hotelTags}
		sliderNav={sliderNav}
		ratio={ratio}
		link={link}
		renderLink={renderLink}
		renderImage={renderImage}>
		<div>
			<Card.Header size={headingSize} as={headingAs} className={styles.hotelHeading}>
				<span>{header}</span>
				<div className={styles.hotelHeading__text} title={subheader}>
					{subheader}
				</div>
			</Card.Header>
			{stars && (
				<div>
					<StarRating rating={stars} />
				</div>
			)}
			<div className={styles.hotelRatingsContainer}>
				<div>
					{minAge >= 16 && <p>{`Adults only ${minAge}+`}</p>}
					{days && (
						<div className={styles.hotelDetailsDays}>
							<span>{labels?.departureDate}</span>
							<span>{days}</span>
							<span>{labels?.allInc && labels?.allInc}</span>
						</div>
					)}
				</div>
				{price && (
					<div className={styles.hotelDetailsPrice}>
						<div className={styles.hotelDetailsSave}>{save}</div>
						{wasPrice && (
							<div className={styles.hotelDetailsWas}>
								{`${labels?.was} `}
								<span>
									<FormattedMoney number={wasPrice} local={local} />
								</span>
							</div>
						)}
						<div className={styles.hotelPriceDetails}>
							{labels?.priceFrom && <span>{labels.priceFrom}</span>}
							<span className={styles.hotelDetailsAmount}>
								<FormattedMoney number={price} local={local} />
							</span>
						</div>
						{labels?.perAdult && (
							<div className={styles.hotelDetailsPerson}>{labels?.perAdult}</div>
						)}
						<div className={styles.hotelDetailsTax}>{labels?.taxAndFee && labels?.taxAndFee}</div>
					</div>
				)}
			</div>
		</div>
	</CardBuilder>
);

export default HotelCard;
export { HotelCard };

HotelCard.propTypes = {
	headAs: PropTypes.oneOf(Object.keys(HeadingAsElement)),
	headSize: PropTypes.oneOf(Object.keys(HeadingSize)),
	header: PropTypes.string.isRequired,
	headingAs: PropTypes.oneOf(Object.keys(HeadingAsElement)),
	headingSize: PropTypes.oneOf(Object.keys(HeadingSize)),
	isSponsored: PropTypes.bool,
	hotelTags: PropTypes.arrayOf(PropTypes.string),
	subheader: PropTypes.string,
	stars: PropTypes.number,
	minAge: PropTypes.number,
	days: PropTypes.string,
	save: PropTypes.string,
	wasPrice: PropTypes.number,
	price: PropTypes.number,
	local: PropTypes.oneOf([LOCAL_FR, LOCAL_EN]),
	labels: PropTypes.shape({
		allInc: PropTypes.string,
		was: PropTypes.string,
		perAdult: PropTypes.string,
		taxAndFee: PropTypes.string,
		departureDate: PropTypes.string,
		priceFrom: PropTypes.string,
	}),
	images: PropTypes.arrayOf(
		PropTypes.shape({
			fluid: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
			fixed: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
			source: PropTypes.shape({
				src: PropTypes.string,
				srcSet: PropTypes.string,
				sizes: PropTypes.string,
			}),
			alt: PropTypes.string,
			src: PropTypes.string,
		})
	),
	image: PropTypes.shape({
		source: PropTypes.shape({
			src: PropTypes.string,
			srcSet: PropTypes.string,
			sizes: PropTypes.string,
		}),
		alt: PropTypes.string,
		src: PropTypes.string,
	}),
	logo: PropTypes.shape({
		source: PropTypes.shape({
			src: PropTypes.string,
			srcSet: PropTypes.string,
			sizes: PropTypes.string,
		}),
		alt: PropTypes.string,
		src: PropTypes.string,
	}),
	logoPosition: PropTypes.oneOf(Object.keys(LogoPositions)),
	renderImage: PropTypes.func,
	renderLink: PropTypes.func,
	button: PropTypes.shape({
		icon: PropTypes.string,
		iconPlacement: PropTypes.oneOf(["right", "left"]),
		onClick: PropTypes.func,
		text: PropTypes.string,
		to: PropTypes.string,
		target: PropTypes.string,
	}),
	/**
	 * CardBuilder props
	 */
	clickable: PropTypes.bool,
	type: PropTypes.oneOf(Object.keys(CardAsType)),
	sliderNav: PropTypes.bool,
	ratio: PropTypes.oneOf(Object.keys(CardImageRatio)),
	link: PropTypes.shape({
		to: PropTypes.string.isRequired,
		target: PropTypes.string,
		text: PropTypes.string,
	}),
};

HotelCard.defaultProps = {
	headAs: HeadingAsElement.h3,
	headSize: HeadingSize.h2,
	headingAs: HeadingAsElement.h3,
	headingSize: HeadingSize.h2,
	logo: null,
	logoPosition: "bottom-left",
	isSponsored: false,
	image: null,
	images: [],
	hotelTags: [],
	subheader: null,
	stars: undefined,
	minAge: undefined,
	days: undefined,
	save: undefined,
	wasPrice: undefined,
	price: undefined,
	local: LOCAL_EN,
	labels: {},
	renderImage: undefined,
	renderLink: undefined,
	button: null,
	clickable: true,
	type: "shadowNew",
	sliderNav: true,
	ratio: "4/3",
	link: null,
};
